import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Progress, List, Select, DatePicker, Segmented, Badge, Tooltip as ATooltip } from 'antd';
import {
  ArrowUpOutlined, ArrowDownOutlined, CreditCardOutlined,
  WifiOutlined, AlertOutlined, DollarOutlined, ReloadOutlined,
  RiseOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadialBarChart, RadialBar, ComposedChart
} from 'recharts';
import dayjs from 'dayjs';

// ── 生成近30天更真实的趋势数据 ──
const genDayData = () => {
  const base = { traffic: 480, order: 30, income: 14000 };
  return Array.from({ length: 30 }, (_, i) => {
    const d = dayjs().subtract(29 - i, 'day');
    const isWeekend = [0, 6].includes(d.day());
    const noise = () => (Math.random() - 0.5) * 0.3;
    const wFactor = isWeekend ? 0.75 : 1;
    return {
      date: d.format('MM/DD'),
      流量: Math.round(base.traffic * wFactor * (1 + noise()) * (1 + i * 0.008)),
      订单: Math.round(base.order * wFactor * (1 + noise()) * (1 + i * 0.006)),
      收入: Math.round(base.income * wFactor * (1 + noise()) * (1 + i * 0.01)),
    };
  });
};

const ALL_TREND = genDayData();

const pieData = [
  { name: '正常在线', value: 38240, color: '#52c41a' },
  { name: '待激活',  value: 6580,  color: '#1890ff' },
  { name: '离线',    value: 3020,  color: '#faad14' },
  { name: '停机',    value: 1650,  color: '#ff4d4f' },
  { name: '异常',    value: 510,   color: '#722ed1' },
];

const operatorData = [
  { name: '移动', value: 28400, color: '#1677ff' },
  { name: '联通', value: 12300, color: '#e6232e' },
  { name: '电信', value: 7800,  color: '#faad14' },
  { name: '海外', value: 1500,  color: '#52c41a' },
];

const channelData = [
  { name: '华南渠道', 本月收入: 42000, 上月收入: 38200, 卡数: 18400 },
  { name: '华东渠道', 本月收入: 35600, 上月收入: 31000, 卡数: 14200 },
  { name: '华北渠道', 本月收入: 28900, 上月收入: 26800, 卡数: 10600 },
  { name: '西南渠道', 本月收入: 12400, 上月收入: 11200, 卡数: 4800 },
  { name: '海外渠道', 本月收入: 9700,  上月收入: 6300,  卡数: 2000 },
];

const topCards = [
  { key: '1', iccid: '8986031921010073421', client: '深圳割草机科技',  usage: '18.6GB', rate: 93, trend: 'up' },
  { key: '2', iccid: '8986031921010073422', client: '广州CPE设备公司', usage: '16.2GB', rate: 81, trend: 'up' },
  { key: '3', iccid: '8986031921010073423', client: '北京VR体验馆',    usage: '14.8GB', rate: 74, trend: 'down' },
  { key: '4', iccid: '8986031921010073424', client: '上海安防系统',    usage: '12.3GB', rate: 62, trend: 'up' },
  { key: '5', iccid: '8986031921010073425', client: '成都对讲机厂商',  usage: '10.1GB', rate: 51, trend: 'down' },
];

const alerts = [
  { type: 'error',   msg: '卡 8986...3421 流量超限，已触发断网', time: '刚刚' },
  { type: 'warning', msg: '客户"北京VR体验馆"有3张卡持续离线', time: '8分钟前' },
  { type: 'warning', msg: '流量池"全球共享池A"剩余不足10%',     time: '25分钟前' },
  { type: 'warning', msg: '卡 8986...3425 套餐即将到期（3天）', time: '1小时前' },
  { type: 'info',    msg: '新工单 #2031 已提交，待处理',        time: '2小时前' },
];

// 自定义 Tooltip
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(0,0,0,0.82)', color: '#fff', borderRadius: 8,
      padding: '10px 14px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)'
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}：{p.name === '收入' ? `¥${p.value.toLocaleString()}` : p.value.toLocaleString()}
          {p.name === '流量' ? ' GB' : p.name === '订单' ? ' 单' : ''}
        </div>
      ))}
    </div>
  );
};

const STAT_CARDS = [
  { title: '总卡数',   value: 50000,  icon: <CreditCardOutlined />,  color: '#1677ff', bg: 'linear-gradient(135deg,#e6f4ff,#bae0ff)', suffix: '张', sub: '+120 今日新增', trend: '+0.24%' },
  { title: '在线卡',   value: 38240,  icon: <WifiOutlined />,         color: '#52c41a', bg: 'linear-gradient(135deg,#f6ffed,#b7eb8f)', suffix: '张', sub: '在线率 76.5%', trend: '+2.3%' },
  { title: '本月流量', value: '4.82', icon: <ThunderboltOutlined />,  color: '#722ed1', bg: 'linear-gradient(135deg,#f9f0ff,#d3adf7)', suffix: 'TB', sub: '较上月 +18%', trend: '+18%' },
  { title: '本月收入', value: 128600, icon: <DollarOutlined />,       color: '#fa8c16', bg: 'linear-gradient(135deg,#fff7e6,#ffd591)', prefix: '¥', sub: '较上月 +12%', trend: '+12%' },
  { title: '异常告警', value: 23,     icon: <AlertOutlined />,        color: '#ff4d4f', bg: 'linear-gradient(135deg,#fff1f0,#ffccc7)', suffix: '条', sub: '待处理 15 条', trend: '-3' },
];

type Range = '7' | '14' | '30';

const Dashboard: React.FC = () => {
  const [range, setRange] = useState<Range>('7');
  const [trendData, setTrendData] = useState(ALL_TREND.slice(-7));
  const [tick, setTick] = useState(0);

  // 模拟实时数字跳动
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const n = parseInt(range);
    setTrendData(ALL_TREND.slice(-n));
  }, [range]);

  const onlineNow = 38240 + (tick % 3) - 1; // 模拟实时波动

  return (
    <div style={{ padding: '8px 16px 16px' }}>

      {/* ── 统计卡片 ── */}
      <Row gutter={[12, 12]}>
        {STAT_CARDS.map((item, i) => (
          <Col xs={12} sm={12} md={8} lg={6} xl={24 / STAT_CARDS.length} key={i}>
            <Card
              bordered={false}
              style={{ borderRadius: 12, background: item.bg, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
              bodyStyle={{ padding: '16px 20px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{item.title}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#1a1a1a', lineHeight: 1 }}>
                    {item.prefix}{i === 1 ? onlineNow.toLocaleString() : typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                    <span style={{ fontSize: 13, fontWeight: 400, marginLeft: 3, color: '#555' }}>{item.suffix}</span>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>{item.sub}</div>
                </div>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: 'rgba(255,255,255,0.7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, color: item.color,
                }}>
                  {item.icon}
                </div>
              </div>
              <div style={{
                marginTop: 10, fontSize: 11,
                color: item.title === '异常告警' ? '#ff4d4f' : '#52c41a',
                display: 'flex', alignItems: 'center', gap: 2,
              }}>
                {item.title === '异常告警' ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
                {item.trend} 较上周
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── 趋势图 + 卡状态饼图 ── */}
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={15}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
            title={<span style={{ fontWeight: 700 }}>流量 / 订单 / 收入趋势</span>}
            extra={
              <Segmented
                size="small"
                value={range}
                onChange={v => setRange(v as Range)}
                options={[
                  { label: '近7天', value: '7' },
                  { label: '近14天', value: '14' },
                  { label: '近30天', value: '30' },
                ]}
              />
            }
          >
            <ResponsiveContainer width="100%" height={230}>
              <ComposedChart data={trendData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradTraffic" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#1677ff" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#1677ff" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#fa8c16" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#fa8c16" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area yAxisId="left"  type="monotone" dataKey="流量" stroke="#1677ff" strokeWidth={2} fill="url(#gradTraffic)" />
                <Area yAxisId="right" type="monotone" dataKey="收入" stroke="#fa8c16" strokeWidth={2} fill="url(#gradIncome)" />
                <Line  yAxisId="left"  type="monotone" dataKey="订单" stroke="#52c41a" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={9}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', height: '100%' }}
            title={<span style={{ fontWeight: 700 }}>卡状态分布</span>}
          >
            <Row align="middle">
              <Col span={12}>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie
                      data={pieData} cx="50%" cy="50%"
                      innerRadius={52} outerRadius={78}
                      dataKey="value" paddingAngle={3}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => `${Number(v).toLocaleString()} 张`} />
                  </PieChart>
                </ResponsiveContainer>
              </Col>
              <Col span={12}>
                {pieData.map(item => (
                  <div key={item.name} style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: '#666' }}>{item.name}</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{item.value.toLocaleString()}</span>
                  </div>
                ))}
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ── 运营商分布 + 渠道收入对比 ── */}
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
            title={<span style={{ fontWeight: 700 }}>运营商分布</span>}
          >
            <ResponsiveContainer width="100%" height={190}>
              <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="90%" data={operatorData}>
                <RadialBar dataKey="value" label={{ position: 'insideStart', fill: '#fff', fontSize: 11 }}>
                  {operatorData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </RadialBar>
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} formatter={(v) => operatorData.find(d => d.value.toString() === v)?.name || v} />
                <Tooltip formatter={(v: any) => `${Number(v).toLocaleString()} 张`} />
              </RadialBarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={16}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
            title={<span style={{ fontWeight: 700 }}>各渠道收入对比（本月 vs 上月）</span>}
          >
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={channelData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: any) => `¥${Number(v).toLocaleString()}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="本月收入" fill="#1677ff" radius={[4, 4, 0, 0]} />
                <Bar dataKey="上月收入" fill="#bae0ff" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* ── TOP5 + 实时告警 ── */}
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={14}>
          <Card
            title={<span style={{ fontWeight: 700 }}>业务量 TOP5</span>}
            bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
          >
            <Table
              dataSource={topCards}
              size="small"
              pagination={false}
              columns={[
                {
                  title: 'ICCID', key: 'iccid', dataIndex: 'iccid', ellipsis: true, width: 160,
                  render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
                },
                { title: '客户', dataIndex: 'client', key: 'client' },
                {
                  title: '用量', dataIndex: 'usage', key: 'usage', width: 75,
                  render: (v: string, r: any) => (
                    <span style={{ color: r.trend === 'up' ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
                      {r.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {v}
                    </span>
                  )
                },
                {
                  title: '套餐使用率', dataIndex: 'rate', key: 'rate', width: 140,
                  render: (v: number) => (
                    <ATooltip title={`${v}%`}>
                      <Progress
                        percent={v} size="small"
                        strokeColor={v > 90 ? '#ff4d4f' : v > 70 ? '#faad14' : '#52c41a'}
                        format={p => `${p}%`}
                      />
                    </ATooltip>
                  )
                },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={<span style={{ fontWeight: 700 }}>实时告警</span>}
            extra={<Badge count={23} style={{ backgroundColor: '#ff4d4f' }} />}
            bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
          >
            <List
              size="small"
              dataSource={alerts}
              renderItem={item => (
                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <Badge
                        status={item.type === 'error' ? 'error' : item.type === 'warning' ? 'warning' : 'processing'}
                      />
                      <span style={{ fontSize: 12, color: '#333', flex: 1 }}>{item.msg}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#bbb', paddingLeft: 14 }}>{item.time}</div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
